import type { PageValidationRule } from "@patient-watch/forms";
import { ui } from "@patient-watch/ui";
import { Label } from "@patient-watch/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@patient-watch/ui/select";
import { Plus, Trash2 } from "lucide-react";
import type { PriorLogicInput } from "./LogicConditionFields";

type RuleType = PageValidationRule["type"];

const RULE_TYPES: { value: RuleType; label: string; description: string }[] = [
    { value: "sum_equals", label: "Must add up to", description: "The answers must total a number." },
    { value: "at_most_input", label: "At most another answer", description: "No higher than an earlier answer." },
    { value: "exclusive", label: '"None of the above"', description: "One box cannot be ticked with the others." },
    { value: "at_least_one", label: "Tick at least one", description: "At least one box must be ticked." },
    { value: "pattern", label: "Text format", description: "A text answer must match a pattern." },
];

function newRule(type: RuleType, pageInputs: PriorLogicInput[]): PageValidationRule {
    const first = pageInputs[0]?.input_id ?? "";
    switch (type) {
        case "sum_equals":
            return { type, input_ids: [], total: 100 };
        case "at_most_input":
            return { type, input_id: first, max_input_id: "" };
        case "exclusive":
            return { type, exclusive_input_id: "", input_ids: [] };
        case "at_least_one":
            return { type, input_ids: [] };
        case "pattern":
            return { type, input_id: first, pattern: "", message: "Check the format of your answer." };
    }
}

function QuestionSelect({
    value,
    questions,
    onChange,
    placeholder,
}: {
    value: string;
    questions: PriorLogicInput[];
    onChange: (inputId: string) => void;
    placeholder: string;
}) {
    return (
        <Select value={value || undefined} onValueChange={onChange}>
            <SelectTrigger size="sm" className="h-8 text-xs">
                <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
                {questions.map((question) => (
                    <SelectItem key={question.input_id} value={question.input_id}>
                        {question.pageLabel}: {question.label}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}

function QuestionChecklist({
    selected,
    questions,
    onChange,
}: {
    selected: string[];
    questions: PriorLogicInput[];
    onChange: (inputIds: string[]) => void;
}) {
    if (questions.length === 0) {
        return <p className="text-2xs text-muted-foreground">No suitable questions on this page yet.</p>;
    }
    return (
        <div className="space-y-1">
            {questions.map((question) => (
                <label key={question.input_id} className="flex items-center gap-2 text-xs">
                    <input
                        type="checkbox"
                        checked={selected.includes(question.input_id)}
                        onChange={(event) =>
                            onChange(
                                event.target.checked
                                    ? [...selected, question.input_id]
                                    : selected.filter((id) => id !== question.input_id)
                            )
                        }
                    />
                    <span className="truncate">{question.label}</span>
                </label>
            ))}
        </div>
    );
}

/** `pages.page_validation`: rules that check several answers on a page together, before Next. */
export function PageValidationEditor({
    rules,
    pageQuestions,
    earlierQuestions,
    onChange,
}: {
    rules: PageValidationRule[];
    /** Questions on this page. */
    pageQuestions: PriorLogicInput[];
    /** Questions on this page and earlier ones. */
    earlierQuestions: PriorLogicInput[];
    onChange: (rules: PageValidationRule[]) => void;
}) {
    const checkboxes = pageQuestions.filter((question) => question.input_key === "checkbox");
    const numbers = pageQuestions.filter((question) =>
        ["number", "count", "slider", "measurement"].includes(question.input_key ?? "")
    );
    const texts = pageQuestions.filter((question) =>
        ["short-text", "long-text", "address-autocomplete"].includes(question.input_key ?? "")
    );
    const update = (index: number, next: PageValidationRule) =>
        onChange(rules.map((rule, i) => (i === index ? next : rule)));

    return (
        <div className="space-y-2">
            <div>
                <Label className="text-xs">Checks before Next</Label>
                <p className="text-muted-foreground text-xs">Rules that look at several answers on this page.</p>
            </div>
            {rules.map((rule, index) => (
                <div key={index} className="space-y-2 bg-muted/40 p-2 rounded-md">
                    <div className="flex items-center gap-2">
                        <Select
                            value={rule.type}
                            onValueChange={(type) => update(index, newRule(type as RuleType, pageQuestions))}
                        >
                            <SelectTrigger size="sm" className="flex-1 h-8 text-xs">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {RULE_TYPES.map((type) => (
                                    <SelectItem key={type.value} value={type.value}>
                                        {type.label} - {type.description}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <ui.Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="w-6 h-6"
                            aria-label="Remove check"
                            onClick={() => onChange(rules.filter((_, i) => i !== index))}
                        >
                            <Trash2 size={14} />
                        </ui.Button>
                    </div>

                    {rule.type === "sum_equals" ? (
                        <>
                            <QuestionChecklist
                                selected={rule.input_ids}
                                questions={numbers}
                                onChange={(input_ids) => update(index, { ...rule, input_ids })}
                            />
                            <ui.Input
                                type="number"
                                aria-label="Total"
                                value={rule.total}
                                className="h-8 text-xs"
                                onChange={(event) => update(index, { ...rule, total: Number(event.target.value) || 0 })}
                            />
                        </>
                    ) : rule.type === "at_most_input" ? (
                        <>
                            <QuestionSelect
                                value={rule.input_id}
                                questions={numbers}
                                placeholder="This answer"
                                onChange={(input_id) => update(index, { ...rule, input_id })}
                            />
                            <QuestionSelect
                                value={rule.max_input_id}
                                questions={earlierQuestions.filter((question) => question.input_id !== rule.input_id)}
                                placeholder="Must not be more than"
                                onChange={(max_input_id) => update(index, { ...rule, max_input_id })}
                            />
                        </>
                    ) : rule.type === "exclusive" ? (
                        <>
                            <QuestionSelect
                                value={rule.exclusive_input_id}
                                questions={checkboxes}
                                placeholder='The "None" box'
                                onChange={(exclusive_input_id) =>
                                    update(index, {
                                        ...rule,
                                        exclusive_input_id,
                                        input_ids: rule.input_ids.filter((id) => id !== exclusive_input_id),
                                    })
                                }
                            />
                            <QuestionChecklist
                                selected={rule.input_ids}
                                questions={checkboxes.filter((box) => box.input_id !== rule.exclusive_input_id)}
                                onChange={(input_ids) => update(index, { ...rule, input_ids })}
                            />
                        </>
                    ) : rule.type === "at_least_one" ? (
                        <QuestionChecklist
                            selected={rule.input_ids}
                            questions={checkboxes}
                            onChange={(input_ids) => update(index, { ...rule, input_ids })}
                        />
                    ) : (
                        <>
                            <QuestionSelect
                                value={rule.input_id}
                                questions={texts}
                                placeholder="Text answer"
                                onChange={(input_id) => update(index, { ...rule, input_id })}
                            />
                            <div className="gap-2 grid grid-cols-[minmax(0,1fr)_4rem]">
                                <ui.Input
                                    aria-label="Pattern"
                                    value={rule.pattern}
                                    placeholder="e.g. ^[A-Z]{1,2}[0-9]"
                                    className="h-8 font-mono text-xs"
                                    onChange={(event) => update(index, { ...rule, pattern: event.target.value })}
                                />
                                <ui.Input
                                    aria-label="Flags"
                                    value={rule.flags ?? ""}
                                    placeholder="i"
                                    className="h-8 font-mono text-xs"
                                    onChange={(event) =>
                                        update(index, { ...rule, flags: event.target.value || undefined })
                                    }
                                />
                            </div>
                        </>
                    )}

                    <ui.Input
                        aria-label="Error message"
                        value={rule.message ?? ""}
                        placeholder={rule.type === "pattern" ? "Error message (required)" : "Error message (optional)"}
                        className="h-8 text-xs"
                        onChange={(event) => {
                            const message = event.target.value;
                            update(
                                index,
                                rule.type === "pattern"
                                    ? { ...rule, message }
                                    : ({ ...rule, message: message || undefined } as PageValidationRule)
                            );
                        }}
                    />
                </div>
            ))}
            <button
                type="button"
                className="flex items-center gap-1 hover:bg-muted px-2 py-1 rounded-md w-full text-muted-foreground hover:text-foreground text-xs"
                onClick={() => onChange([...rules, newRule("at_least_one", pageQuestions)])}
            >
                <Plus size={14} /> Add check
            </button>
        </div>
    );
}
